import { useCallback, useEffect, useState } from 'react';

import type { BriefReadView } from '../../api/client.ts';

/**
 * What the Brief step holds open of its history: whether the history is shown, the past version
 * on screen instead of the current Brief, and the version whose View takes the focus back once
 * that view closes. Held by the step, so it outlives the history block being drawn again.
 *
 * A version is open only while the Brief on screen still has it as a past version: if a read
 * answers without it, or with it as the current version, it is closed. Given another study, the
 * history starts again closed, with no version open.
 */
export const useBriefHistory = (
  studyId: string,
  brief: BriefReadView['brief'],
): {
  open: boolean;
  toggle: () => void;
  viewing: number | null;
  view: (version: number) => void;
  back: () => void;
  restoreFocusTo: number | null;
  focusRestored: () => void;
} => {
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);
  const [restoreFocusTo, setRestoreFocusTo] = useState<number | null>(null);
  const [historyFor, setHistoryFor] = useState(studyId);
  if (historyFor !== studyId) {
    setHistoryFor(studyId);
    setOpen(false);
    setViewing(null);
    setRestoreFocusTo(null);
  }

  useEffect(() => {
    if (
      viewing !== null &&
      brief !== null &&
      (viewing === brief.version || !brief.history.some((entry) => entry.version === viewing))
    ) {
      setViewing(null);
    }
  }, [brief, viewing]);

  const toggle = useCallback(() => setOpen((shown) => !shown), []);
  const back = useCallback(() => {
    setRestoreFocusTo(viewing);
    setViewing(null);
  }, [viewing]);
  const focusRestored = useCallback(() => setRestoreFocusTo(null), []);

  return {
    open,
    toggle,
    viewing,
    view: setViewing,
    back,
    restoreFocusTo,
    focusRestored,
  };
};
